import { PDFDocument } from "pdf-lib";
import { getDownloadURL, getStorage, ref, uploadBytes } from "firebase/storage";
import { app } from "~/services/firebase";
import { v4 as uuidv4 } from "uuid";

export default async function uploadFile(file, path = "") {
  let files = {};
  const fileUuid = uuidv4();
  const nameSplit = file.name.split(".");
  const type = nameSplit[nameSplit.length - 1];
  const storage = getStorage(app);
  const local = path ? path + "/" + fileUuid + "." + type : fileUuid + "." + type;
  const imageRef = ref(storage, local);
  await uploadBytes(imageRef, file).then(async (snapshot) => {
    await getDownloadURL(snapshot.ref).then((downloadURL) => {
      files = { url: downloadURL, name: fileUuid + "." + type, size: file.size };
    });
  });
  return files;
}

export function organizeMultiAndSingleFiles(documents, path = "") {
  return documents.map(async (document) => {
    if (!document.file_id) return null;
    const documentId = document.document_id;
    const fileId = document.file_id;
    if (Array.isArray(fileId) || (fileId.length !== undefined && typeof fileId !== "string")) {
      const filesArray = Array.from(fileId).sort((a, b) => a.size - b.size);
      const uploadPromises = filesArray.map((file) => processAndFormatFile(file, path, documentId));
      return Promise.all(uploadPromises);
    } else {
      return processAndFormatFile(fileId, path, documentId);
    }
  });
}

async function fileToArrayBuffer(file) {
  return await file.arrayBuffer();
}

async function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = () => resolve(reader.result);
    reader.readAsDataURL(file);
  });
}

function dataUrlToUint8Array(dataUrl) {
  const bin = atob(dataUrl.split(",")[1]);
  const len = bin.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
}

async function imageFileToPdfBytes(file) {
  let imgBytes = null;
  let format = "jpeg";
  if (file.type === "image/jpeg" || file.type === "image/jpg") {
    imgBytes = new Uint8Array(await fileToArrayBuffer(file));
    format = "jpeg";
  } else if (file.type === "image/png") {
    imgBytes = new Uint8Array(await fileToArrayBuffer(file));
    format = "png";
  } else {
    const dataUrl = await fileToDataUrl(file);
    const img = new Image();
    await new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = reject;
      img.src = dataUrl;
    });
    const canvas = document.createElement("canvas");
    canvas.width = img.naturalWidth || img.width;
    canvas.height = img.naturalHeight || img.height;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0);
    const pngDataUrl = canvas.toDataURL("image/png");
    imgBytes = dataUrlToUint8Array(pngDataUrl);
    format = "png";
  }
  const pdfDoc = await PDFDocument.create();
  const embedded = format === "png" ? await pdfDoc.embedPng(imgBytes) : await pdfDoc.embedJpg(imgBytes);
  const page = pdfDoc.addPage([embedded.width, embedded.height]);
  page.drawImage(embedded, { x: 0, y: 0, width: embedded.width, height: embedded.height });
  const pdfBytes = await pdfDoc.save();
  return new Uint8Array(pdfBytes);
}

async function processAndFormatFile(file, path, documentId) {
  const originalName = file?.name || "file";
  const mime = file?.type || "";
  let toUpload = file;
  let returnedType = "pdf";
  if (mime === "application/pdf" || /\.pdf$/i.test(originalName)) {
    returnedType = "pdf";
  } else if (mime.startsWith("image/") || /\.(png|jpe?g|webp|bmp|gif)$/i.test(originalName)) {
    const pdfBytes = await imageFileToPdfBytes(file);
    const base = originalName.replace(/\.[^.]+$/, "");
    toUpload = new File([pdfBytes], `${base}.pdf`, { type: "application/pdf" });
    returnedType = "pdf";
  } else {
    returnedType = mime.split("/").pop() || "bin";
  }
  const uploadedFile = await uploadFile(toUpload, path);
  const normalizedName = (toUpload.name || originalName).normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  const key = uploadedFile.name;
  return { ...uploadedFile, key, name: normalizedName, type: returnedType, document_id: documentId };
}